import { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Text, Button, SimpleGrid } from '@chakra-ui/react';

import { ProcessingState } from '@assets/images';
import { DatePicker2 } from '@components/datePicker2';
import { MODE_CONSTRAINTS, RANGE_CONSTRAINTS } from '@components/datePicker2/const';

import { debounce } from '@helpers/utils';
import Select from '@components/filter/select';

import { useSnackbarNotification } from '@store/snackbarNotification';
import { useConfirmModal } from '@store/useConfirmModal';
import useGetSelloutMonitoring from '@domain/selloutMonitoring/useSelloutMonitoring';
import { useGetCustomerList } from '@domain/selloutMonitoring/useGetCustomerList';

import { useFilter } from '@pages/SelloutMonitoring/hooks/state/filters';
import { initialStateGlobalFilter, useGlobalFilter } from '@pages/SelloutMonitoring/hooks/state/globalFilter';
import { useGetGroupType } from '@domain/groupType/useGroupType';
import { useGetCustomerGroup } from '@domain/customerGroup/useCustomerGroup';

export default function GlobalFilter() {
    const confirmModal = useConfirmModal();
    const { set, reset, ...rest } = useGlobalFilter();
    const { setFilter, resetFilter, onClearFilter, ...restLocal } = useFilter();
    const [appliedFilter, setAppliedFilter] = useState(rest);
    const isSameFilter = JSON.stringify(rest) === JSON.stringify(appliedFilter);

    const { onChangeSnackbarNotification } = useSnackbarNotification();
    const isShowSnackbar = useRef(false);

    const params = useMemo(
        () => ({
            ...rest,
            ...restLocal,
        }),
        [rest, restLocal],
    );

    const { isLoading, isFetched, isError, isSuccess } = useGetSelloutMonitoring({ ...params });
    const isDisableButton = isSameFilter || isLoading;

    const [search, setSearch] = useState(initialStateGlobalFilter);
    const handleChange = (key) => (value) => setAppliedFilter((prev) => ({ ...prev, [key]: value }));
    const onCloseOptions = () => setSearch(initialStateGlobalFilter);
    const handleSearch = (key) => (value) => setSearch((prev) => ({ ...prev, [key]: value }));
    const onClearOptions = (key) => () => {
        setAppliedFilter((prev) => ({ ...prev, [key]: '' }));
        setSearch((prev) => ({ ...prev, [key]: '' }));
    };

    const custGroup = useGetCustomerGroup({
        enabled: !!search?.custGroup,
        custGroup: search?.custGroup,
        groupType: appliedFilter?.groupType?.name ?? ['sap', 'tradee'],
    });

    const groupType = useGetGroupType({ enabled: true });

    const customerList = useGetCustomerList({
        enabled: !!search?.customer,
        search: { group: appliedFilter?.group?.name, customer: search?.customer },
    });

    const FILTER_CONFIG = [
        {
            type: 'select',
            props: {
                title: 'Group Type',
                placeholder: 'Pilih group type',
                data: groupType?.data,
                refetchData: groupType?.refetch,
                isLoading: groupType?.isLoading,
                selected: appliedFilter?.groupType,
                onChangeFilter: handleChange('groupType'),
                onSearch: debounce(handleSearch('groupType')),
                onCloseOptions,
                onClearOptions: onClearOptions('groupType'),
                isDimBg: true,
                helperText: 'Akan ada perbedaan data list customer terkait dengan pilihan group type.',
            },
        },
        {
            type: 'select',
            props: {
                title: 'Customer Group',
                placeholder: 'Pilih customer group',
                data: custGroup?.data,
                refetchData: custGroup?.refetch,
                isLoading: custGroup?.isLoading,
                selected: appliedFilter?.custGroup,
                onChangeFilter: handleChange('custGroup'),
                onSearch: debounce(handleSearch('custGroup')),
                onCloseOptions,
                onClearOptions: onClearOptions('custGroup'),
                isDimBg: true,
                isShowId: true,
            },
        },
        {
            type: 'select',
            props: {
                title: 'Customer Id',
                placeholder: 'Pilih customer id',
                data: customerList?.data,
                refetchData: customerList?.refetch,
                isLoading: customerList?.isLoading,
                selected: appliedFilter?.customer,
                onChangeFilter: handleChange('customer'),
                onSearch: debounce(handleSearch('customer')),
                onCloseOptions,
                onClearOptions: onClearOptions('customer'),
                isDimBg: true,
                isShowId: true,
            },
        },
        {
            type: 'date',
            props: {
                label: 'Sellout Date',
                placeholder: 'Pilih tanggal sellout',
                mode: MODE_CONSTRAINTS.RANGE,
                constraint: RANGE_CONSTRAINTS.SAME_MONTH,
                value: appliedFilter?.sellout_date,
                onChange: handleChange('sellout_date'),
                isDimBg: true,
                isIconRight: false,
                maxDate: new Date(),
                tooltipLabel: (
                    <>
                        <Text color={'semantic.200'} mb={2}>
                            Sellout Date
                        </Text>
                        <Text color={'semantic.200'}>
                            Rentang tanggal yang dapat dipilih hanya rentang tanggal dalam bulan yang sama
                        </Text>
                    </>
                ),
            },
        },
        {
            type: 'select',
            props: {
                title: 'Sellout Value',
                data: [
                    { id: 'sellout_value_store', name: 'Customer Price' },
                    { id: 'sellout_value_paragon', name: 'HET Paragon' },
                ],
                selected: appliedFilter?.sellout_value,
                onChangeFilter: handleChange('sellout_value'),
                isDimBg: true,
                hideCloseIcon: true,
            },
        },
    ];

    const renderFilter = (item, i) => {
        if (item.type === 'select')
            return <Select key={i} isMultiple={false} {...item.props} capitalizeOption={true} />;
        if (item.type === 'date') return <DatePicker2 key={i} {...item.props} />;
    };

    const handleSubmit = () => {
        if (!appliedFilter?.group && !appliedFilter?.customer) {
            return confirmModal.set({
                isOpen: true,
                title: 'Lengkapi Data Terlebih Dulu',
                desc: 'Pilih dan lengkapi untuk menampilkan data sellout yang diinginkan.',
                isShowBtnNo: false,
                btnLabelYes: 'Mengerti',
                isDanger: false,
                ConfirmationStateImage: ProcessingState,
            });
        }
        set({ ...appliedFilter, enabled: true });
        setAppliedFilter((prev) => ({ ...prev, enabled: true }));
        isShowSnackbar.current = true;
    };

    useEffect(() => {
        if (isShowSnackbar.current && !isLoading) {
            if (isSuccess) {
                onChangeSnackbarNotification({
                    title: 'Menampilkan Data Sellout',
                    description: 'Data sellout yang dipilih berhasil ditampilkan',
                    status: 'success',
                    duration: 3,
                });
                isShowSnackbar.current = false;
            } else if (isError) {
                onChangeSnackbarNotification({
                    title: 'Gagal Menampilkan Data Sellout',
                    description: 'Terjadi gangguan pada saat proses pengambilan data',
                    status: 'error',
                    duration: 3,
                });
                isShowSnackbar.current = false;
            }
        }
    }, [isFetched, isLoading, isSuccess, isError]);

    return (
        <Box bg="white" p="24px" border="1px solid" borderColor="neutral.100" borderRadius="8px" boxShadow="sm">
            <Text fontWeight="bold" fontSize="24px">
                Sellout Monitoring
            </Text>

            <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} gap="16px" alignItems="start" mt="16px">
                {FILTER_CONFIG.map(renderFilter)}

                <Button maxW="200px" onClick={handleSubmit} disabled={isDisableButton} alignSelf={'end'}>
                    Tampilkan Data
                </Button>
            </SimpleGrid>
        </Box>
    );
}
